import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Plus, Save, FileText, CheckCircle2, AlertCircle, X, Layers, RefreshCw, MapPin } from 'lucide-react';
import { OrderLine, UnitType } from '@/types';
import { ordersApi, customersApi, customerAddressesApi, productsApi } from '@/services/api';
import type { CustomerAddressDto } from '@/types/locations';
import { LineItem } from '@/components/LineItem';
import { SearchableSelect, Option } from '@/components/SearchableSelect';
import { Button } from "@/components/ui/button";
import { routesApi } from '@/services/api';
import { PalletPreviewPanel } from '@/components/PalletPreviewPanel';
import type { PalletPreviewResponse, PalletType } from '@/types/loading-plan';
import { PACKING_ALGORITHMS } from '@/types/loading-plan';
import { palletTypesApi } from '@/services/loading-plan-api';

export interface OrderFormState {
  totalsByUnit: { unit: string; total: number }[];
  isSubmitting: boolean;
}

interface OrderFormProps {
  orderId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  isDialog?: boolean;
  hideFooter?: boolean;
  onStateChange?: (state: OrderFormState) => void;
}

export interface OrderFormRef {
  submit: () => void;
  getTotalItems: () => number;
  isSubmitting: () => boolean;
}

export const OrderForm = forwardRef<OrderFormRef, OrderFormProps>((
{
  orderId: propOrderId, 
  onSuccess, 
  onCancel,
  isDialog = false,
  hideFooter = false,
  onStateChange
}, ref) => {
  const navigate = useNavigate();
  const { id: paramsOrderId } = useParams<{ id?: string }>();
  const orderId = propOrderId || paramsOrderId;
  
  // Helper para obtener fecha de hoy en formato YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [customerId, setCustomerId] = useState('');
  const [orderDate, setOrderDate] = useState(getTodayDate());
  const [routeId, setRouteId] = useState<string | null>(null);
  const [lines, setLines] = useState<OrderLine[]>([
    { id: crypto.randomUUID(), productId: '', productName: '', quantity: 0, unit: 'cajas', observations: null }
  ]);
  const [observations, setObservations] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState(!!orderId);
  const [palletPreview, setPalletPreview] = useState<PalletPreviewResponse | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [algorithm, setAlgorithm] = useState<string>('ShelfNextFit');
  const [palletTypeId, setPalletTypeId] = useState<string | undefined>(undefined);
  const [availablePalletTypes, setAvailablePalletTypes] = useState<PalletType[]>([]);

  // Direcciones de entrega del cliente seleccionado
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddressDto[]>([]);
  const [customerAddressId, setCustomerAddressId] = useState<string | null>(null);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  // Cuando se carga una comanda existente, evitamos sobreescribir la dirección
  // ya snapshotada al re-fetchear las direcciones del cliente.
  const skipNextAddressAutoSelectRef = useRef(false);

  // Refs para navegación por teclado
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Exponer métodos mediante ref
  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(new Event('submit') as any),
    getTotalItems: () => lines.reduce((acc, curr) => acc + curr.quantity, 0),
    isSubmitting: () => isSubmitted
  }));
  const [initialCustomerOption, setInitialCustomerOption] = useState<Option | null>(null);

  // Emitir cambios de state al padre
  useEffect(() => {
    if (onStateChange) {
      const totalsByUnit = lines.reduce((acc, line) => {
        const existing = acc.find(t => t.unit === line.unit);
        if (existing) {
          existing.total += line.quantity;
        } else {
          acc.push({ unit: line.unit, total: line.quantity });
        }
        return acc;
      }, [] as { unit: string; total: number }[]);

      onStateChange({
        totalsByUnit,
        isSubmitting: isSubmitted
      });
    }
  }, [lines, isSubmitted, onStateChange]);

  // Resetear fecha al día actual cuando no hay orderId (modo creación)
  useEffect(() => {
    if (!orderId) {
      setOrderDate(getTodayDate());
      setCustomerId('');
      setInitialCustomerOption(null);
      setObservations('');
      setCustomerAddresses([]);
      setCustomerAddressId(null);
      setLines([{ id: crypto.randomUUID(), productId: '', productName: '', quantity: 0, unit: 'cajas', observations: null }]);
    }
  }, [orderId]);

  // Cargar direcciones del cliente seleccionado. En creación: auto-selecciona la
  // default (o primera). En edición: respeta la dirección ya snapshotada en la
  // comanda gracias a skipNextAddressAutoSelectRef.
  useEffect(() => {
    if (!customerId) {
      setCustomerAddresses([]);
      setCustomerAddressId(null);
      return;
    }

    let cancelled = false;
    setIsLoadingAddresses(true);
    customerAddressesApi.getAll(customerId)
      .then(list => {
        if (cancelled) return;
        setCustomerAddresses(list);
        if (skipNextAddressAutoSelectRef.current) {
          // La comanda ya trae deliveryCustomerAddressId fijado en el effect de carga
          skipNextAddressAutoSelectRef.current = false;
          return;
        }
        const def = list.find(a => a.isDefault) ?? list[0];
        setCustomerAddressId(def?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setCustomerAddresses([]);
          setCustomerAddressId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAddresses(false);
      });

    return () => { cancelled = true; };
  }, [customerId]);

  // Cargar orden si estamos editando
  useEffect(() => {
    if (orderId) {
      const loadOrder = async () => {
        try {
          setIsLoadingOrder(true);
          const order = await ordersApi.getById(orderId);
          // Marca para que el effect de carga de direcciones NO pise la dirección
          // ya snapshotada al hacer el fetch de direcciones del cliente.
          skipNextAddressAutoSelectRef.current = !!order.deliveryCustomerAddressId;
          setCustomerAddressId(order.deliveryCustomerAddressId || null);
          setCustomerId(order.customerId || '');
          setOrderDate(order.orderDate || getTodayDate());
          setRouteId(order.routeId || null);
          // palletTypeId del pedido; si no tiene, se resolverá en el effect de pallet types
          if (order.palletTypeId) {
            setPalletTypeId(order.palletTypeId);
          }
          setObservations(order.observations || '');
          
          if (order.customerId && order.customerName) {
            setInitialCustomerOption({
                value: order.customerId,
                label: order.customerName
            });
          }
          
          // Map order lines
          if (order.lines && order.lines.length > 0) {
            setLines(order.lines.map(line => ({
              id: line.id,
              productId: line.productId || '',
              productName: line.productName || '', 
              packagingContainerId: line.packagingContainerId,
              packagingContainerName: line.packagingContainerName,
              quantity: line.quantity,
              unit: line.unit as UnitType,
              observations: line.observations
            })));
          } else {
            setLines([{ id: crypto.randomUUID(), productId: '', productName: '', quantity: 0, unit: 'cajas', observations: null }]);
          }
        } catch (err: any) {
          console.error('Error loading order:', err);
          setError(err.message || 'Error al cargar la comanda');
        } finally {
          setIsLoadingOrder(false);
        }
      };
      loadOrder();
    }
  }, [orderId]);

  // Search handler for customers
  const handleCustomerSearch = useCallback(async (query: string): Promise<Option[]> => {
    try {
      const response = await customersApi.getAll({
        search: query,
        isActive: true,
        pageSize: 50
      });
      return response.items.map(c => ({
        value: c.id,
        label: c.name || '',
        subLabel: c.externalId || undefined
      }));
    } catch (err) {
      console.error('Error searching customers:', err);
      return [];
    }
  }, []);

  const handleRouteSearch = useCallback(async (query: string): Promise<Option[]> => {
    try {
      const routes = await routesApi.getAll({
        search: query,
        isActive: true
      });
      return routes.map(r => ({
        value: r.id,
        label: `${r.name} (${r.code})`,
      }));
    } catch (err) {
      console.error('Error searching routes:', err);
      return [];
    }
  }, []);

  // Search handler for products
  const handleProductSearch = useCallback(async (query: string): Promise<Option[]> => {
    try {
      const response = await productsApi.getAll({
        search: query,
        isActive: true,
        pageSize: 50
      });
      return response.items.map(p => ({
        value: p.id,
        label: p.name || '',
        subLabel: p.externalId || undefined
      }));
    } catch (err) {
      console.error('Error searching products:', err);
      return [];
    }
  }, []);

  // Cargar tipos de palet disponibles
  useEffect(() => {
    palletTypesApi.getAll().then(types => {
      setAvailablePalletTypes(types);
      setPalletTypeId(prev => {
        // Si ya tiene valor y es un id válido en la lista, mantenerlo
        if (prev && types.some(t => t.id === prev)) return prev;
        // Sino, EPAL 1 por defecto o el primero de la lista
        const epal = types.find(t => t.code === 'EPAL 1');
        return epal?.id ?? types[0]?.id;
      });
    }).catch(() => {});
  }, []);

  // Auto-cálculo de preview con debounce
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const validLines = lines.filter(l => l.productId && l.quantity > 0);
    if (!validLines.length || !palletTypeId) {
      setPalletPreview(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setIsLoadingPreview(true);
      ordersApi.palletPreview({
        lines: validLines.map(l => ({
          productId: l.productId,
          packagingContainerId: l.packagingContainerId ?? null,
          quantity: Math.ceil(l.quantity),
        })),
        palletTypeId,
        algorithm,
      }).then(setPalletPreview)
        .catch(() => {})
        .finally(() => setIsLoadingPreview(false));
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [lines, algorithm, palletTypeId]);

  const handleAddLine = useCallback(() => {
    const newLine: OrderLine = {
      id: crypto.randomUUID(),
      productId: '',
      productName: '',
      quantity: 0,
      unit: 'cajas',
      observations: null
    };
    setLines(prev => [...prev, newLine]);
  }, []);

  const handleLineComplete = useCallback((lineId: string) => {
    const lineIndex = lines.findIndex(l => l.id === lineId);
    const isLastLine = lineIndex === lines.length - 1;
    if (isLastLine) handleAddLine();
  }, [lines, handleAddLine]);

  const handleRemoveLine = useCallback((id: string) => {
    if (lines.length === 1) {
      alert("La comanda debe tener al menos una línea.");
      return;
    }
    setLines(prev => prev.filter(line => line.id !== id));
  }, [lines.length]);

  const handleUpdateLine = useCallback((id: string, field: keyof OrderLine, value: string | number) => {
    setLines(prev => prev.map(line => {
      if (line.id === id) {
        return { ...line, [field]: value };
      }
      return line;
    }));
  }, []);

  const handlePalletPreview = useCallback(async () => {
    const validLines = lines.filter(l => l.productId && l.quantity > 0);
    if (!validLines.length || !palletTypeId) return;
    setIsLoadingPreview(true);
    try {
      const result = await ordersApi.palletPreview({
        lines: validLines.map(l => ({
          productId: l.productId,
          packagingContainerId: l.packagingContainerId ?? null,
          quantity: Math.ceil(l.quantity),
        })),
        palletTypeId,
        algorithm,
      });
      setPalletPreview(result);
    } catch (err) {
      console.error('Error previewing pallets:', err);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [lines, algorithm, palletTypeId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customerId) {
      alert("Por favor seleccione un cliente.");
      return;
    }
    const hasInvalidLines = lines.some(l => !l.productId || l.quantity <= 0);
    if (hasInvalidLines) {
      alert("Por favor revise las líneas de pedido. Faltan productos o cantidades.");
      return;
    }

    setIsSubmitted(true);
    setError(null);
    
    try {
      const orderData = {
        customerId: customerId,
        orderDate: orderDate,
        routeId: routeId || null,
        observations: observations.trim() || null,
        palletTypeId: palletTypeId || null,
        customerAddressId: customerAddressId || null,
        lines: lines.map(line => ({
          productId: line.productId,
          packagingContainerId: line.packagingContainerId,
          quantity: line.quantity,
          unit: line.unit,
          observations: line.observations ? line.observations.trim() : null
        }))
      };

      if (orderId) {
        await ordersApi.update(orderId, orderData);
      } else {
        await ordersApi.create(orderData);
      }
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/comandas');
      }
    } catch (err: any) {
      console.error('Error saving order:', err);
      setError(err.message || `Error al ${orderId ? 'actualizar' : 'crear'} la comanda`);
      setIsSubmitted(false);
    }
  };

  const totalItems = lines.reduce((acc, curr) => acc + curr.quantity, 0);

  if (isLoadingOrder) {
    return (
      <div className="w-full flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando comanda...</p>
        </div>
      </div>
    );
  }

  const formContent = (
    <form id="order-form" onSubmit={handleSubmit} className={`${isDialog ? 'flex flex-col flex-1 overflow-hidden' : 'bg-white rounded-xl shadow-sm border border-green-100 overflow-hidden'}`}>
      {/* Form Header / Meta Data */}
      <div className={`${isDialog ? 'p-4 flex-shrink-0' : 'bg-green-50/50 px-6 py-6 border-b border-green-100'} grid grid-cols-1 md:grid-cols-3 gap-6`}>
        <div>
          <SearchableSelect
            label="Cliente / Destinatario"
            labelClassName="block text-sm font-semibold text-green-800 mb-1"
            placeholder="Buscar cliente por nombre o ID..."
            value={customerId}
            onChange={(value) => setCustomerId(value)}
            onSearch={handleCustomerSearch}
            minSearchLength={2}
            initialOption={initialCustomerOption}
          />
        </div>
        <div>
          <SearchableSelect
            label="Ruta"
            labelClassName="block text-sm font-semibold text-green-800 mb-1"
            placeholder="Seleccionar ruta (opcional)"
            value={routeId || ''}
            onChange={(value) => setRouteId(value || null)}
            onSearch={handleRouteSearch}
            minSearchLength={0}
          />
        </div>
        <div>
          <label htmlFor="date" className="block text-sm font-semibold text-green-800 mb-1">
            Fecha de Entrega
          </label>
          <input
            id="date"
            ref={dateInputRef}
            type="date"
            value={orderDate}
            onChange={(e) => setOrderDate(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const firstProductInput = document.querySelector('[data-line-index="0"] input[placeholder*="Buscar producto"]') as HTMLInputElement;
                firstProductInput?.focus();
              }
            }}
            className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-12"
          />
        </div>

        {/* Dirección de entrega: snapshot del cliente al guardar */}
        <div className="md:col-span-3">
          <label htmlFor="delivery-address" className="block text-sm font-semibold text-green-800 mb-1 flex items-center gap-1.5">
            <MapPin className="h-4 w-4 text-green-600" />
            Dirección de entrega
          </label>
          {!customerId ? (
            <p className="text-xs text-slate-500 italic h-12 flex items-center">
              Selecciona primero un cliente.
            </p>
          ) : isLoadingAddresses ? (
            <p className="text-xs text-slate-500 italic h-12 flex items-center">
              Cargando direcciones…
            </p>
          ) : customerAddresses.length === 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Este cliente no tiene direcciones registradas. La comanda se guardará sin dirección de entrega y no aparecerá en la vista de carga.
            </p>
          ) : (
            <select
              id="delivery-address"
              value={customerAddressId ?? ''}
              onChange={(e) => setCustomerAddressId(e.target.value || null)}
              className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-12"
            >
              {customerAddresses.map(addr => {
                const parts = [
                  addr.addressLine,
                  [addr.postalCode, addr.cityName].filter(Boolean).join(' '),
                ].filter(Boolean).join(' — ');
                const label = addr.name
                  ? `${addr.name} · ${parts}`
                  : parts;
                return (
                  <option key={addr.id} value={addr.id}>
                    {addr.isDefault ? '★ ' : ''}{label}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      </div>

      {/* Dynamic Lines Area */}
      <div className={isDialog ? 'p-4 flex-1 overflow-y-auto' : 'p-6'}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-green-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-600" />
            Líneas de Pedido
          </h2>
        </div>

        <div className="space-y-3 mb-6">
          {lines.map((line, index) => (
            <LineItem
              key={line.id}
              index={index}
              line={line}
              onUpdate={handleUpdateLine}
              onRemove={handleRemoveLine}
              onProductSearch={handleProductSearch}
              onLineComplete={handleLineComplete}
            />
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleAddLine}
          className="w-full py-6 border-dashed border-2 border-green-200 text-green-700 hover:bg-green-50 hover:border-green-400"
        >
          <Plus className="h-5 w-5 mr-2" />
          Añadir Producto
        </Button>

        {/* Pallet Preview - justo tras las líneas */}
        <div className="mt-6 border border-green-200 rounded-lg bg-green-50/30 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-green-800 flex items-center gap-2">
              <Layers className="h-4 w-4 text-green-600" />
              Previsualización de palets
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isLoadingPreview || !lines.some(l => l.productId && l.quantity > 0)}
              onClick={handlePalletPreview}
              className="text-green-700 border-green-300 hover:bg-green-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoadingPreview ? 'animate-spin' : ''}`} />
              {isLoadingPreview ? 'Calculando...' : 'Recalcular'}
            </Button>
          </div>

          {/* Selectores de algoritmo y tipo de palet */}
          <div className="flex flex-wrap gap-3 mb-3">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Algoritmo de montaje</label>
              <select
                value={algorithm}
                onChange={(e) => setAlgorithm(e.target.value)}
                className="w-full bg-white border border-green-200 text-slate-900 text-xs rounded-md p-2 focus:ring-green-500 focus:border-green-500"
              >
                {PACKING_ALGORITHMS.map(a => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </select>
            </div>
            {availablePalletTypes.length > 0 && (
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-slate-500 mb-1">Tipo de palet</label>
                <select
                  value={palletTypeId || ''}
                  onChange={(e) => setPalletTypeId(e.target.value || undefined)}
                  className="w-full bg-white border border-green-200 text-slate-900 text-xs rounded-md p-2 focus:ring-green-500 focus:border-green-500"
                >
                  {availablePalletTypes.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.name} ({pt.lengthCm}x{pt.widthCm}cm)</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {palletPreview && <PalletPreviewPanel preview={palletPreview} />}
          {!palletPreview && !isLoadingPreview && (
            <p className="text-xs text-slate-400 italic">
              La previsualización se calcula automáticamente al añadir productos.
            </p>
          )}
        </div>
      </div>

      {/* Observations */}
      <div className={isDialog ? 'p-4' : 'px-6'}>
        <div className="mb-6">
          <label htmlFor="observations" className="block text-sm font-semibold text-green-800 mb-2">
            Observaciones de Producción
          </label>
          <textarea
            id="observations"
            rows={2}
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-3"
            placeholder="Ej. Calibre especial, etiquetas personalizadas..."
          />
        </div>
      </div>

      {/* Footer Area: Submit (solo si no está hideFooter) */}
      {!hideFooter && (
        <div className={`${isDialog ? 'p-4 shrink-0 border-t border-green-100' : 'bg-green-50/30 px-6 py-8 border-t border-green-100'}`}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-green-100">
            <div className="text-slate-600 text-sm font-medium">
              Total: <span className="font-bold text-slate-900">{totalItems}</span> unidades/kilos.
            </div>
            
            <div className="flex gap-3 w-full sm:w-auto">
              {isDialog && (
                <Button type="button" variant="outline" onClick={onCancel} className="flex-1 sm:flex-none">
                  Cancelar
                </Button>
              )}
              <Button
                type="submit"
                disabled={isSubmitted}
                className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 font-bold"
              >
                {isSubmitted ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 mr-2 animate-bounce" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    {orderId ? 'Actualizar comanda' : 'Confirmar comanda'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );

  if (isDialog) return formContent;

  return (
    <div className="w-full max-w-5xl mx-auto py-6">
      <div className="flex justify-between items-center mb-6 px-4 sm:px-0">
        <h1 className="text-2xl font-bold text-slate-900">
          {orderId ? 'Editar Comanda' : 'Nueva Comanda'}
        </h1>
        <Button variant="ghost" onClick={() => navigate('/comandas')}>
          <X className="mr-2 h-4 w-4" /> Cancelar
        </Button>
      </div>
      
      {error && (
        <div className="mx-4 sm:mx-0 mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-600 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="mx-4 sm:mx-0">
        {formContent}
      </div>
    </div>
  );
});
